import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy } from "lucide-react";

interface AddUserFormProps {
  onAddChild: (firstName: string, lastName: string, username: string, role: 'child' | 'parent') => Promise<string>;
}

export function AddUserForm({ onAddChild }: AddUserFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<'child' | 'parent'>('child');
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const { toast } = useToast();

  const validateEmail = (email: string): boolean => {
    const gmailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/i;
    const isValid = gmailRegex.test(email);
    
    if (!isValid) {
      setEmailError("Please enter a valid Gmail address");
    } else {
      setEmailError("");
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted", { firstName, lastName, username, role });
    
    if (!firstName.trim() || !lastName.trim() || !username.trim() || !role) {
      toast({
        title: "Missing Fields",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(username.trim())) {
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Calling onAddChild with:", firstName.trim(), lastName.trim(), username.trim(), role);
      const inviteUrl = await onAddChild(firstName.trim(), lastName.trim(), username.trim().toLowerCase(), role);
      console.log("Got invite URL:", inviteUrl);
      setInviteUrl(inviteUrl);
      toast({
        title: "Success!",
        description: `${role === 'child' ? 'Child' : 'Parent'} added successfully. Invitation URL generated.`,
      });
    } catch (error) {
      console.error("Error adding user:", error);
      toast({
        title: "Error",
        description: `Failed to add ${role}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyInviteUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast({
      title: "Copied!",
      description: "Invitation URL copied to clipboard.",
    });
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setUsername("");
    setRole('child');
    setEmailError("");
    setInviteUrl("");
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const handleOpenChange = (open: boolean) => {
    console.log("Dialog open state changing to:", open);
    setIsOpen(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button 
          className="gap-2"
          onClick={() => {
            console.log("Add User button clicked");
            setIsOpen(true);
          }}
        >
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        {!inviteUrl ? (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Add a new user</DialogTitle>
              <DialogDescription>
                Add a child or parent to your family account.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="role" className="text-right">
                  Role *
                </Label>
                <Select value={role} onValueChange={(value: 'child' | 'parent') => setRole(value)}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="firstName" className="text-right">
                  First name *
                </Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="lastName" className="text-right">
                  Last name *
                </Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="username" className="text-right whitespace-nowrap">
                  Email *
                </Label>
                <div className="col-span-3 space-y-1">
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (emailError) validateEmail(e.target.value);
                    }}
                    className={emailError ? "border-red-500" : ""}
                    required
                    placeholder="e.g. user@gmail.com"
                    type="email"
                  />
                  {emailError && (
                    <p className="text-xs text-red-500">{emailError}</p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : `Add ${role === 'child' ? 'Child' : 'Parent'}`}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Invitation Generated</DialogTitle>
              <DialogDescription>
                Send this URL to the user to complete their registration.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-2">
                <Input value={inviteUrl} readOnly className="flex-1" />
                <Button size="sm" onClick={copyInviteUrl}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
